import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";

// Public
import WelcomePage from "@/pages/public/WelcomePage";
import LoginPage from "@/pages/public/LoginPage";
import RegisterPage from "@/pages/public/RegisterPage";
import OnboardingOrganizerPage from "@/pages/public/OnboardingOrganizerPage";
import ChangePasswordPage from "@/pages/public/ChangePasswordPage";
import ForgotPasswordPage from "@/pages/public/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/public/ResetPasswordPage";
import TermsPage from "@/pages/public/TermsPage";
import PrivacyPage from "@/pages/public/PrivacyPage";

// Buyer
import EventsListPage from "@/pages/buyer/EventsListPage";
import EventDetailPage from "@/pages/buyer/EventDetailPage";
import TicketPage from "@/pages/buyer/TicketPage";
import MyTicketsPage from "@/pages/buyer/MyTicketsPage";
import ProfilePage from "@/pages/buyer/ProfilePage";
import MyEngagementsPage from "@/pages/buyer/MyEngagementsPage";
import NotificationPrefsPage from "@/pages/buyer/NotificationPrefsPage";
import PublicOrganizerPage from "@/pages/public/PublicOrganizerPage";
import FundraisersListPage from "@/pages/buyer/FundraisersListPage";
import FundraiserDetailPage from "@/pages/buyer/FundraiserDetailPage";
import ParticipatePage from "@/pages/buyer/ParticipatePage";
import VotePage from "@/pages/buyer/VotePage";
import SurveyPage from "@/pages/buyer/SurveyPage";
import RedeemPage from "@/pages/buyer/RedeemPage";
import MyVouchersPage from "@/pages/buyer/MyVouchersPage";
import VoucherDetailPage from "@/pages/buyer/VoucherDetailPage";
import VoucherClaimPage from "@/pages/buyer/VoucherClaimPage";

// Organizer
import OrgDashboardPage from "@/pages/organizer/OrgDashboardPage";
import OrgEventsPage from "@/pages/organizer/OrgEventsPage";
import OrgCreatePage from "@/pages/organizer/OrgCreatePage";
import OrgEventDetailPage from "@/pages/organizer/OrgEventDetailPage";
import OrgSeatMapPage from "@/pages/organizer/OrgSeatMapPage";
import OrgTablePlanPage from "@/pages/organizer/OrgTablePlanPage";
import AgentsManagePage from "@/pages/organizer/AgentsManagePage";
import EngagementCreatePage from "@/pages/organizer/EngagementCreatePage";
import OrgProfilePage from "@/pages/organizer/OrgProfilePage";
import VoteElectoralListPage from "@/pages/organizer/VoteElectoralListPage";
import OrgVouchersListPage from "@/pages/organizer/OrgVouchersListPage";
import OrgVoucherCreatePage from "@/pages/organizer/OrgVoucherCreatePage";
import OrgVoucherBatchDetailPage from "@/pages/organizer/OrgVoucherBatchDetailPage";
import VoucherScanPage from "@/pages/organizer/VoucherScanPage";
import OrgGiftListPage from "@/pages/organizer/OrgGiftListPage";

// Public gift list (guest mode)
import PublicGiftListPage from "@/pages/public/PublicGiftListPage";
import PublicTablePlanPage from "@/pages/public/PublicTablePlanPage";

// Magic-link landing
import MagicLinkLandingPage from "@/pages/auth/MagicLinkLandingPage";

// Agent
import AgentScanPage from "@/pages/agent/AgentScanPage";
import AgentHistoryPage from "@/pages/agent/AgentHistoryPage";

// Admin
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminModerationPage from "@/pages/admin/AdminModerationPage";
import AdminPayoutsPage from "@/pages/admin/AdminPayoutsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 60, textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!user) return <WelcomePage />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "organizer" && user.needs_onboarding) return <Navigate to="/onboarding-organizer" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "organizer") return <Navigate to="/org" replace />;
  if (user.role === "agent") return <Navigate to="/agent/scan" replace />;
  return <Navigate to="/events" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShell>
            <Routes>
              {/* Smart root */}
              <Route path="/" element={<RootRedirect />} />

              {/* Public auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/onboarding-organizer" element={<ProtectedRoute roles={["organizer", "admin"]}><OnboardingOrganizerPage /></ProtectedRoute>} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/cgv" element={<TermsPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />

              {/* Buyer / public */}
              <Route path="/events" element={<EventsListPage />} />
              <Route path="/event/:id" element={<EventDetailPage />} />
              <Route path="/ticket/:id" element={<TicketPage />} />
              <Route path="/tickets" element={<ProtectedRoute><MyTicketsPage /></ProtectedRoute>} />
              <Route path="/engagements" element={<ProtectedRoute><MyEngagementsPage /></ProtectedRoute>} />
              <Route path="/profile/notifications" element={<ProtectedRoute><NotificationPrefsPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/o/:slug" element={<PublicOrganizerPage />} />
              <Route path="/fundraisers" element={<FundraisersListPage />} />
              <Route path="/fundraiser/:id" element={<FundraiserDetailPage />} />
              <Route path="/participer" element={<ParticipatePage />} />
              <Route path="/votes" element={<Navigate to="/participer?tab=votes" replace />} />
              <Route path="/surveys" element={<Navigate to="/participer?tab=surveys" replace />} />
              <Route path="/vote/:id" element={<VotePage />} />
              <Route path="/survey/:id" element={<SurveyPage />} />
              <Route path="/redeem" element={<RedeemPage />} />
              <Route path="/vouchers" element={<ProtectedRoute><MyVouchersPage /></ProtectedRoute>} />
              <Route path="/voucher/:id" element={<VoucherDetailPage />} />
              <Route path="/voucher-claim" element={<VoucherClaimPage />} />
              <Route path="/liste/:slug" element={<PublicGiftListPage />} />
              <Route path="/table/:token" element={<PublicTablePlanPage />} />
              <Route path="/auth/magic/:token" element={<MagicLinkLandingPage />} />

              {/* Organizer */}
              <Route path="/org" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgDashboardPage /></ProtectedRoute>} />
              <Route path="/org/events" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgEventsPage /></ProtectedRoute>} />
              <Route path="/org/create" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgCreatePage /></ProtectedRoute>} />
              <Route path="/org/edit/:id" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgCreatePage /></ProtectedRoute>} />
              <Route path="/org/event/:id" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgEventDetailPage /></ProtectedRoute>} />
              <Route path="/org/event/:id/seat-map" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgSeatMapPage /></ProtectedRoute>} />
              <Route path="/org/event/:id/table-plan" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgTablePlanPage /></ProtectedRoute>} />
              <Route path="/org/event/:id/gift-list" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgGiftListPage /></ProtectedRoute>} />
              <Route path="/org/agents/:eventId" element={<ProtectedRoute roles={["organizer", "admin"]}><AgentsManagePage /></ProtectedRoute>} />
              <Route path="/org/engagement" element={<ProtectedRoute roles={["organizer", "admin"]}><EngagementCreatePage /></ProtectedRoute>} />
              <Route path="/org/vote/:id/electoral-list" element={<ProtectedRoute roles={["organizer", "admin"]}><VoteElectoralListPage /></ProtectedRoute>} />
              <Route path="/org/vouchers" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgVouchersListPage /></ProtectedRoute>} />
              <Route path="/org/voucher-create" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgVoucherCreatePage /></ProtectedRoute>} />
              <Route path="/org/voucher-batch/:id" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgVoucherBatchDetailPage /></ProtectedRoute>} />
              <Route path="/org/voucher-scan" element={<ProtectedRoute roles={["organizer", "admin"]}><VoucherScanPage /></ProtectedRoute>}/>
              <Route path="/org/profile" element={<ProtectedRoute roles={["organizer", "admin"]}><OrgProfilePage /></ProtectedRoute>} />

              {/* Agent */}
              <Route path="/agent/scan" element={<ProtectedRoute roles={["agent", "organizer", "admin"]}><AgentScanPage /></ProtectedRoute>} />
              <Route path="/agent/history" element={<ProtectedRoute roles={["agent", "organizer", "admin"]}><AgentHistoryPage /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboardPage /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsersPage /></ProtectedRoute>} />
              <Route path="/admin/moderation" element={<ProtectedRoute roles={["admin"]}><AdminModerationPage /></ProtectedRoute>} />
              <Route path="/admin/payouts" element={<ProtectedRoute roles={["admin"]}><AdminPayoutsPage /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettingsPage /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppShell>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
